import { memo, useCallback } from 'react';

import { useTrackDepletedUpsellClicked } from '@air/analytics';
import { Banner } from '@air/primitive-banner';
import { Button } from '@air/primitive-button';
import { useAirModal } from '@air/provider-modal';

import { PlansModal } from '~/components/Modals/PlansModal/PlansModal';
import { type WorkspaceImportsBanner as WorkspaceImportsBannerModel } from '~/components/v2/components/workspace/workspace-imports/workspaceImportTypes';
import { useCreditAnalyticsParams } from '~/hooks/useCreditAnalyticsParams';

export type WorkspaceImportsBannerProps = {
  banner: WorkspaceImportsBannerModel;
};

export const WorkspaceImportsBanner = memo(({ banner }: WorkspaceImportsBannerProps) => {
  const [showPlansModal] = useAirModal(PlansModal);
  const { trackDepletedUpsellClicked } = useTrackDepletedUpsellClicked();
  const creditAnalyticsParams = useCreditAnalyticsParams();

  const handleUpgradeClick = useCallback(() => {
    if (banner.tracksCreditUpsell && creditAnalyticsParams) {
      trackDepletedUpsellClicked({
        surface: 'banner',
        ...creditAnalyticsParams,
      });
    }

    showPlansModal();
  }, [banner.tracksCreditUpsell, creditAnalyticsParams, showPlansModal, trackDepletedUpsellClicked]);

  return (
    <div className="px-4" data-testid="WORKSPACE_IMPORTS_BANNER">
      <Banner
        appearance="warning"
        className="text-12"
        suffix={
          banner.canUpgrade ? (
            <Button
              appearance="ghost"
              className="mt-1 font-medium text-inherit underline"
              data-testid="WORKSPACE_IMPORTS_BANNER_UPGRADE"
              onClick={handleUpgradeClick}
              size="small"
            >
              Upgrade
            </Button>
          ) : undefined
        }
      >
        {banner.message}
      </Banner>
    </div>
  );
});

WorkspaceImportsBanner.displayName = 'WorkspaceImportsBanner';
